import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, MapPin } from "lucide-react";
import { api, ASSET_URL } from "../lib/api";
import { useSite } from "../lib/site-context";
import { applySeo } from "../lib/seo";

const dateText = (startsAt, endsAt, lang) => {
  if (!startsAt) return "";
  const locale = lang === "en" ? "en-GB" : "de-DE";
  const zone = "Europe/Berlin";
  const start = new Date(startsAt);
  const date = new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "long", year: "numeric", timeZone: zone,
  }).format(start);
  const time = value => new Intl.DateTimeFormat(locale, {
    hour: "2-digit", minute: "2-digit", timeZone: zone,
  }).format(new Date(value));
  if (!endsAt) return lang === "en" ? `${date} at ${time(startsAt)}` : `${date} · ${time(startsAt)} Uhr`;
  return lang === "en"
    ? `${date} · ${time(startsAt)} to ${time(endsAt)}`
    : `${date} · ${time(startsAt)} bis ${time(endsAt)} Uhr`;
};

export default function EventsIndexPage() {
  const { lang, t, settings } = useSite();
  const [items, setItems] = React.useState([]);
  const [media, setMedia] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const baseUrl = (settings?.public_base_url || "https://dr-b-digitec.com").replace(/\/$/, "");
    applySeo({
      slug: "ueber-uns/veranstaltungen",
      lang,
      siteName: settings?.site_name || "Dr. B.-Digitec",
      baseUrl,
      seo: {
        title: lang === "en" ? "Events & Academy – Dr. B.-Digitec" : "Veranstaltungen & Akademie – Dr. B.-Digitec",
        description: lang === "en"
          ? "Current events, workshops and continuing education by Dr. B.-Digitec for dental practices and dental professionals."
          : "Aktuelle Veranstaltungen, Workshops und Fortbildungen von Dr. B.-Digitec für Zahnarztpraxen und dentale Fachkräfte.",
        robots: "index,follow",
      },
    });
  }, [lang, settings]);

  React.useEffect(() => {
    Promise.all([api.events().catch(() => []), api.media("image").catch(() => [])])
      .then(([events, assets]) => {
        const active = (events || []).filter(event => event.published !== false && event.status !== "cancelled");
        active.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        setItems(active);
        setMedia(assets || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const mediaMap = React.useMemo(() => Object.fromEntries(media.map(asset => [asset.id, asset])), [media]);

  return (
    <main className="pt-36 md:pt-44 pb-24" data-testid="events-index-page">
      <section className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-3"><div className="brut-tag">{t("Veranstaltungen", "Events")}</div></div>
          <div className="col-span-12 md:col-span-9">
            <h1 className="brut-h1 mb-6">{t("Veranstaltungen, die Wissen in die Praxis bringen.", "Events that turn knowledge into practice.")}</h1>
            <p className="brut-body max-w-3xl opacity-80">{t("Workshops, Fortbildungen und Termine für Zahnarztpraxen rund um digitale Workflows und modernen Zahnersatz.", "Workshops, training and events for dental practices covering digital workflows and modern dental prosthetics.")}</p>
          </div>
        </div>

        {loading ? <p className="brut-body">{t("Veranstaltungen werden geladen …", "Loading events …")}</p> : items.length === 0 ? (
          <div className="admin-card p-8 md:p-12"><h2 className="brut-h2 mb-4">{t("Derzeit sind keine Veranstaltungen veröffentlicht.", "There are currently no published events.")}</h2><p className="brut-body">{t("Neue Termine erscheinen automatisch an dieser Stelle.", "New dates will automatically appear here.")}</p></div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {items.map(event => {
              const title = lang === "en" ? (event.title_en || event.title_de) : event.title_de;
              const excerpt = lang === "en" ? (event.excerpt_en || event.excerpt_de) : event.excerpt_de;
              const asset = event.cover_media_id ? mediaMap[event.cover_media_id] : null;
              const image = asset?.variants?.webp || asset?.variants?.thumb || asset?.variants?.original;
              const past = event.ends_at && new Date(event.ends_at) < new Date();
              return (
                <Link key={event.id} to={`/akademie/${event.slug}`} className="group block border-2 border-[var(--ink)] rounded-[28px] overflow-hidden bg-[var(--bg)] shadow-xl hover:-translate-y-1 transition-transform">
                  {image && <img src={ASSET_URL(image)} alt={asset?.[lang === "en" ? "alt_en" : "alt_de"] || title} className="w-full aspect-[16/10] object-cover" loading="lazy" />}
                  <div className="p-6 md:p-8">
                    <div className="flex flex-wrap items-center gap-3 mb-5">
                      <span className="brut-tag !py-2 !px-3">{past ? t("Rückblick", "Past event") : event.status === "sold_out" ? t("Ausgebucht", "Sold out") : t("Veranstaltung", "Event")}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm font-bold mb-2"><CalendarDays size={17} className="shrink-0 mt-0.5"/><span>{dateText(event.starts_at, event.ends_at, lang) || t("Termin folgt", "Date to follow")}</span></div>
                    {(event.venue_name || event.venue_address) && <div className="flex items-start gap-2 text-sm opacity-70 mb-5"><MapPin size={17} className="shrink-0 mt-0.5"/><span>{[event.venue_name, event.venue_address].filter(Boolean).join(" · ")}</span></div>}
                    <h2 className="text-2xl md:text-3xl font-black leading-tight tracking-[-0.035em] mb-4">{title}</h2>
                    {excerpt && <p className="brut-body opacity-75 mb-6">{excerpt}</p>}
                    <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider">{t("Details & Anmeldung", "Details & registration")} <ArrowUpRight size={14}/></span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
}
